import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Edit, Toolbar, useNotify, useRefresh } from "react-admin";
import { useTranslate } from "react-admin";

import { useErrorHandler } from "../hooks/useErrorHandler";
import { useImageUploadHandler } from "../hooks/useImageUploadHandler";
import { color } from "../theme/designTokens";
import { BaseModalProps } from "../types/modal";
import { CloseButton, SaveButton } from "./buttons";
import { PatientForm, PatientFormData } from "./PatientForm";

type PatientEditTransformOptions = {
  previousData?: PatientFormData;
};

const PatientEditToolbar = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <Toolbar>
      <SaveButton />
      <CloseButton onClick={onCancel} />
    </Toolbar>
  );
};

const PatientEdit = ({ open, onClose }: BaseModalProps) => {
  const { handleImageUpload } = useImageUploadHandler<PatientFormData>();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleError = useErrorHandler();
  const translate = useTranslate();

  const handleTransform = (data: PatientFormData, options?: PatientEditTransformOptions) => {
    const transformedData: PatientFormData = { ...data, locationId: data.locationId || null };

    if ((data.locationId || null) === (options?.previousData?.locationId || null)) {
      delete transformedData.locationId;
    }

    return handleImageUpload(transformedData);
  };

  const handleSuccess = () => {
    notify("edit.patient.success", { type: "success" });
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: color.background }}>{translate("edit.patient.title")}</DialogTitle>
      <DialogContent>
        <Edit
          resource="Patient"
          transform={handleTransform}
          mutationMode="optimistic"
          mutationOptions={{
            onError: error => handleError(error, translate("edit.patient.error")),
            onSuccess: handleSuccess,
          }}
          actions={false}
        >
          <PatientForm toolbar={<PatientEditToolbar onCancel={onClose} />} />
        </Edit>
      </DialogContent>
    </Dialog>
  );
};

export default PatientEdit;
